import { log, supabase } from ".";

/**
 * Obtiene datos IPSOS para una categoría y país específico
 * @param {string} pais - 'Chile' o 'Perú'
 * @param {string} categoria - 'automotriz', 'smartphones', etc.
 * @returns {Object} Datos estructurados de IPSOS
 */
export const getIpsosData = async (pais, categoria) => {
  try {
    const data = await supabase.select("ipsos_categorias", {
      filters: {
        pais,
        categoria,
      },
    });

    if (!data || data.length === 0) {
      log.warn(`No hay datos IPSOS para ${pais} - ${categoria}`);
      return null;
    }

    // Transformar array de registros a objeto estructurado
    const resultado = {
      pais,
      categoria,
      esDatosPendientes: data.some((d) => d.notas?.includes("pendiente")),
      fuente: data[0]?.fuente || "IPSOS",
    };

    // Convertir cada variable a propiedad del objeto
    data.forEach(({ variable, valor, metadata, notas }) => {
      resultado[variable] = {
        valor: Number(valor),
        metadata: metadata || {},
        notas,
      };
    });

    return resultado;
  } catch (error) {
    log.error("getIpsosData", error);
    throw error;
  }
};

/**
 * Procesa IPSOS_AUTOMOTRIZ (src/data/ipsos2025/automotriz.js) para cálculos.
 * Hasta Fase 3 de /categorias, recibía el resultado crudo de getIpsosData (fila por fila de
 * `ipsos_categorias`, anon=0 filas); ahora recibe el módulo de datos, mismos valores.
 * @param {Object} ipsosAutomotriz - IPSOS_AUTOMOTRIZ
 * @returns {Object} Datos procesados listos para cálculos
 */
export const procesarIpsosAutomotriz = (ipsosAutomotriz) => {
  if (!ipsosAutomotriz) return null;

  const { tieneAuto, tieneSegundoAuto, compra0km, compraUsado, frecuencias } = ipsosAutomotriz;

  return {
    // Tenencia
    tieneAuto: tieneAuto.valor,
    tieneSegundoAuto: tieneSegundoAuto.valor,
    // Tipo compra
    compra0km: compra0km.valor,
    compraUsado: compraUsado.valor,
    // Frecuencias
    frecuencias: {
      gamaAlta: {
        share: frecuencias.gamaAlta.share.valor,
        factor: frecuencias.gamaAlta.factor.valor,
        rango: frecuencias.gamaAlta.rango,
      },
      gamaMedia1: {
        share: frecuencias.gamaMedia1.share.valor,
        factor: frecuencias.gamaMedia1.factor.valor,
        rango: frecuencias.gamaMedia1.rango,
      },
      gamaMedia2: {
        share: frecuencias.gamaMedia2.share.valor,
        factor: frecuencias.gamaMedia2.factor.valor,
        rango: frecuencias.gamaMedia2.rango,
      },
      noCompra: {
        share: frecuencias.noCompra.share.valor,
        factor: frecuencias.noCompra.factor.valor,
        rango: frecuencias.noCompra.rango,
      },
    },
  };
};

/**
 * Calcula mercado automotriz basado en datos IPSOS
 * @param {number} totalEjecutivos - Base de ejecutivos
 * @param {Object} ipsos - Datos procesados de IPSOS
 * @param {Object} precios - Precios seleccionados por el usuario
 * @returns {Object} Resultados del cálculo
 */
export const calcularMercadoAutomotriz = (totalEjecutivos, ipsos, precios) => {
  if (!ipsos || !totalEjecutivos) return null;

  // Paso 1: Calcular autos totales
  const conAuto = Math.round(totalEjecutivos * ipsos.tieneAuto);
  const segundoAuto = Math.round(conAuto * ipsos.tieneSegundoAuto);
  const autosTotales = conAuto + segundoAuto;

  // Paso 2: Split por tipo de compra
  const autos0km = Math.round(autosTotales * ipsos.compra0km);
  const autosUsados = Math.round(autosTotales * ipsos.compraUsado);

  // Paso 3: Calcular unidades por año según frecuencia
  const { frecuencias } = ipsos;

  // Gama Alta (1-2 años)
  const gamaAlta0kmAnual = Math.round(autos0km * frecuencias.gamaAlta.share * frecuencias.gamaAlta.factor);
  const gamaAltaUsadoAnual = Math.round(autosUsados * frecuencias.gamaAlta.share * frecuencias.gamaAlta.factor);

  // Gama Media (3-4 años + 5-9 años combinados)
  const gamaMedia0kmAnual =
    Math.round(autos0km * frecuencias.gamaMedia1.share * frecuencias.gamaMedia1.factor) +
    Math.round(autos0km * frecuencias.gamaMedia2.share * frecuencias.gamaMedia2.factor);

  const gamaMediaUsadoAnual =
    Math.round(autosUsados * frecuencias.gamaMedia1.share * frecuencias.gamaMedia1.factor) +
    Math.round(autosUsados * frecuencias.gamaMedia2.share * frecuencias.gamaMedia2.factor);

  // Paso 4: Calcular mercado en USD
  const mercadoGamaAlta0km = gamaAlta0kmAnual * precios.gamaAlta.okm;
  const mercadoGamaAltaUsado = gamaAltaUsadoAnual * precios.gamaAlta.usado;
  const mercadoGamaMedia0km = gamaMedia0kmAnual * precios.gamaMedia.okm;
  const mercadoGamaMediaUsado = gamaMediaUsadoAnual * precios.gamaMedia.usado;

  const mercadoGamaAltaTotal = mercadoGamaAlta0km + mercadoGamaAltaUsado;
  const mercadoGamaMediaTotal = mercadoGamaMedia0km + mercadoGamaMediaUsado;
  const mercadoTotal = mercadoGamaAltaTotal + mercadoGamaMediaTotal;

  return {
    // Funnel
    totalEjecutivos,
    conAuto,
    segundoAuto,
    autosTotales,
    autos0km,
    autosUsados,
    // Unidades anuales
    gamaAlta: {
      okm: { anual: gamaAlta0kmAnual, mensual: Math.round(gamaAlta0kmAnual / 12) },
      usado: { anual: gamaAltaUsadoAnual, mensual: Math.round(gamaAltaUsadoAnual / 12) },
      totalUnidades: gamaAlta0kmAnual + gamaAltaUsadoAnual,
    },
    gamaMedia: {
      okm: { anual: gamaMedia0kmAnual, mensual: Math.round(gamaMedia0kmAnual / 12) },
      usado: { anual: gamaMediaUsadoAnual, mensual: Math.round(gamaMediaUsadoAnual / 12) },
      totalUnidades: gamaMedia0kmAnual + gamaMediaUsadoAnual,
    },
    // Mercado USD
    mercado: {
      gamaAlta: {
        okm: mercadoGamaAlta0km,
        usado: mercadoGamaAltaUsado,
        total: mercadoGamaAltaTotal,
      },
      gamaMedia: {
        okm: mercadoGamaMedia0km,
        usado: mercadoGamaMediaUsado,
        total: mercadoGamaMediaTotal,
      },
      totalAnual: mercadoTotal,
      totalMensual: Math.round(mercadoTotal / 12),
    },
    // Resumen personas que compran por mes
    personasCompranMes: {
      okm: Math.round((gamaAlta0kmAnual + gamaMedia0kmAnual) / 12),
      usado: Math.round((gamaAltaUsadoAnual + gamaMediaUsadoAnual) / 12),
      total: Math.round((gamaAlta0kmAnual + gamaAltaUsadoAnual + gamaMedia0kmAnual + gamaMediaUsadoAnual) / 12),
    },
  };
};

/**
 * Procesa datos IPSOS de smartphones para cálculos
 * @param {Object} ipsosData - Datos crudos de getIpsosData
 * @returns {Object} Datos procesados listos para cálculos
 */
export const procesarIpsosSmartphones = (ipsosData) => {
  if (!ipsosData) return null;

  return {
    pais: ipsosData.pais,
    esDatosPendientes: ipsosData.esDatosPendientes,
    fuente: ipsosData.fuente,
    // Tenencia
    tieneSmartphone: ipsosData.tiene_smartphone?.valor || 0,
    tieneCorporativo: ipsosData.tiene_corporativo?.valor || 0,
    // Frecuencias de renovación
    frecuenciaRenovacion: {
      cadaAno: ipsosData.freq_cada_ano?.valor || 0,
      cada2Anos: ipsosData.freq_cada_2_anos?.valor || 0,
      cada3AnosMas: ipsosData.freq_cada_3_anos_mas?.valor || 0,
      cada4Anos: ipsosData.freq_cada_4_anos?.valor || 0,
    },
    // Market share por marca
    marcas: {
      Apple: ipsosData.marca_apple?.valor || 0,
      Samsung: ipsosData.marca_samsung?.valor || 0,
      Xiaomi: ipsosData.marca_xiaomi?.valor || 0,
      Honor: ipsosData.marca_honor?.valor || 0,
      Motorola: ipsosData.marca_motorola?.valor || 0,
      Otros: ipsosData.marca_otros?.valor || 0,
    },
    // Precios default
    preciosDefault: {
      gamaAlta: ipsosData.precio_gama_alta?.valor || 1200,
      gamaMedia: ipsosData.precio_gama_media?.valor || 750,
    },
  };
};

/**
 * Calcula mercado smartphones basado en datos IPSOS
 * @param {number} totalEjecutivos - Base de ejecutivos
 * @param {Object} ipsos - Datos procesados de IPSOS
 * @param {Object} precios - Precios seleccionados { gamaAlta, gamaMedia }
 * @returns {Object} Resultados del cálculo
 */
export const calcularMercadoSmartphones = (totalEjecutivos, ipsos, precios) => {
  if (!ipsos || !totalEjecutivos) return null;

  // Paso 1: Calcular smartphones totales
  const conSmartphone = Math.round(totalEjecutivos * ipsos.tieneSmartphone);
  const conCorporativo = Math.round(totalEjecutivos * ipsos.tieneCorporativo);
  const smartphonesTotales = conSmartphone + conCorporativo;

  // Paso 2: Segmentar por gama según frecuencia de renovación
  // Gama Alta: renuevan cada 1-2 años (frecuencia alta = mayor poder adquisitivo)
  const pctGamaAlta = ipsos.frecuenciaRenovacion.cadaAno + ipsos.frecuenciaRenovacion.cada2Anos;
  // Gama Media: renuevan cada 3+ años
  const pctGamaMedia = ipsos.frecuenciaRenovacion.cada3AnosMas + ipsos.frecuenciaRenovacion.cada4Anos;

  const smartphonesGamaAlta = Math.round(smartphonesTotales * pctGamaAlta);
  const smartphonesGamaMedia = Math.round(smartphonesTotales * pctGamaMedia);

  // Paso 3: Calcular unidades anuales según frecuencia promedio
  const frecuenciaGamaAlta = 1.5; // promedio entre 1 y 2 años
  const frecuenciaGamaMedia = 3.5; // promedio entre 3 y 4+ años

  const unidadesAnualesGamaAlta = Math.round(smartphonesGamaAlta / frecuenciaGamaAlta);
  const unidadesAnualesGamaMedia = Math.round(smartphonesGamaMedia / frecuenciaGamaMedia);

  // Paso 4: Calcular mercado en USD
  const mercadoGamaAlta = unidadesAnualesGamaAlta * precios.gamaAlta;
  const mercadoGamaMedia = unidadesAnualesGamaMedia * precios.gamaMedia;
  const mercadoTotal = mercadoGamaAlta + mercadoGamaMedia;

  return {
    // Funnel
    totalEjecutivos,
    conSmartphone,
    conCorporativo,
    smartphonesTotales,
    // Por gama
    gamaAlta: {
      smartphones: smartphonesGamaAlta,
      pct: pctGamaAlta,
      unidadesAnuales: unidadesAnualesGamaAlta,
      mercado: mercadoGamaAlta,
    },
    gamaMedia: {
      smartphones: smartphonesGamaMedia,
      pct: pctGamaMedia,
      unidadesAnuales: unidadesAnualesGamaMedia,
      mercado: mercadoGamaMedia,
    },
    // Totales
    mercadoTotal,
    unidadesTotales: unidadesAnualesGamaAlta + unidadesAnualesGamaMedia,
    // Marcas (para simulador de oportunidad)
    marcas: ipsos.marcas,
  };
};
